import {useEffect} from 'react';

import type Props from './typings';

const focusableSelectors = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
];

export const isElementFocusable = (element: HTMLElement) => {
  if (element.hasAttribute('disabled')) {
    return false;
  }
  const ariaHidden = element.getAttribute('aria-hidden');

  if (ariaHidden && ariaHidden.toLowerCase() === 'true') {
    return false;
  }
  return true;
};

/**
 * @description Get all focusable elements inside the container div
 * @param {HTMLDivElement} container container div
 * @return {HTMLElement[]} array of focusable html elements
 */
export const getFocusableElements = (container: HTMLDivElement): HTMLElement[] => {
  const focusableElements = Array.from(
    container.querySelectorAll<HTMLElement>(focusableSelectors.join(',')),
  ).filter(isElementFocusable);

  /**
   * Ensure all focusable elements are focusable
   * particularly for Safari, which may not focus elements without explicit tabindex
   */
  focusableElements.forEach((el) => {
    el.setAttribute('tabindex', '0');
  });

  return focusableElements;
};

/**
 * @description hook to trap focus inside the open container ( modal, popup or menu ), when the container is open
 * - focus the container or the first focusable element
 * - cycle/trap focus on `'Tab'` key press, so the focus doesn't leave the open container
 * - callback on `'Escape'` key press ( for example, to close the modal )
 * @param {object} params
 * @param {RefObject<HTMLDivElement> | null} params.containerRef ref to the visible container element
 * @param {Function?} params.onEscKeydown (optional) callback on the ESC key press
 * @param {number?} params.slideAnimationDuration (optional, default 0) duration of the mount/unmount animation
 * @param {boolean?} params.isInitialFocus (optional) whether to focus the first focusable element initially
 * @return {void} void/effect-only hook
 */
const useFocusTrap = ({
  containerRef,
  onEscKeydown,
  slideAnimationDuration = 0,
  isInitialFocus,
}: Props) => {
  useEffect(() => {
    if (!containerRef?.current) return;
    const container = containerRef.current;

    const focusableElements = getFocusableElements(container);

    /**
     * Focus the first focusable element or the container itself
     * Add a small delay to ensure rendering is complete (especially for Safari)
     */
    setTimeout(() => {
      container.focus();

      if (focusableElements.length && isInitialFocus) {
        focusableElements[0].focus();
      }
    }, slideAnimationDuration + 100);

    /**
     * @description Handle keydown events
     * - Close the menu on `'Escape'` key press
     * - Cycle/trap focus on `'Tab'` key press
     * @param {KeyboardEvent} event keyboard event
     * @return {void} void/procedure
     */
    const handleKeyDown = (event: KeyboardEvent) => {
      if (onEscKeydown && event.key === 'Escape') onEscKeydown();
      if (event.key !== 'Tab') return;
      if (!focusableElements.length) return;

      const first = focusableElements[0];
      const last = focusableElements[focusableElements.length - 1];

      if (document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    container.addEventListener('keydown', handleKeyDown);

    /**
     * @description Cleanup function to remove the event listener
     * and prevent memory leaks on unmount or when dependency changes
     */
    return () => {
      container.removeEventListener('keydown', handleKeyDown);
    };
  }, [containerRef, isInitialFocus]);
};

export default useFocusTrap;
